import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const SURFACE = cn(
  'fb-pop z-[60] min-w-44 overflow-hidden rounded-2xl bg-surface p-1.5 shadow-float dark:border dark:border-line',
);

const ITEM = cn(
  'flex cursor-default items-center gap-2 rounded-xl px-2.5 text-sm text-ink outline-none select-none',
  // Mobil eine Fingerbreite hoch, damit man nicht den Nachbarpunkt trifft.
  'py-2.5 sm:py-1.5',
  'data-[highlighted]:bg-sunken data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-3',
);

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({
  className,
  align = 'end',
  sideOffset = 6,
  children,
}: {
  className?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content align={align} sideOffset={sideOffset} className={cn(SURFACE, className)}>
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

export function MenuItem({
  className,
  danger,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenu.Item> & { danger?: boolean }) {
  return (
    <DropdownMenu.Item
      className={cn(
        ITEM,
        danger && 'text-danger data-[highlighted]:bg-danger-soft [&_svg]:text-danger',
        className,
      )}
      {...props}
    />
  );
}

export function MenuCheckboxItem({
  className,
  checked,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenu.CheckboxItem>) {
  return (
    <DropdownMenu.CheckboxItem className={cn(ITEM, 'pl-7 relative', className)} checked={checked} {...props}>
      <DropdownMenu.ItemIndicator className="absolute left-2">
        <Check className="size-3.5" aria-hidden />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.CheckboxItem>
  );
}

export function MenuRadioGroup(props: React.ComponentPropsWithoutRef<typeof DropdownMenu.RadioGroup>) {
  return <DropdownMenu.RadioGroup {...props} />;
}

export function MenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenu.RadioItem>) {
  return (
    <DropdownMenu.RadioItem className={cn(ITEM, 'relative pl-7', className)} {...props}>
      <DropdownMenu.ItemIndicator className="absolute left-2">
        <Check className="size-3.5" aria-hidden />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.RadioItem>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-line" />;
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">
      {children}
    </DropdownMenu.Label>
  );
}

// ── Popover ────────────────────────────────────────────────────────────────

export const Pop = Popover.Root;
export const PopTrigger = Popover.Trigger;
export const PopAnchor = Popover.Anchor;

export function PopContent({
  className,
  align = 'start',
  sideOffset = 6,
  children,
}: {
  className?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  children: React.ReactNode;
}) {
  return (
    <Popover.Portal>
      <Popover.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'fb-pop z-[60] rounded-xl border border-line bg-surface p-3 shadow-pop',
          'w-[min(22rem,calc(100vw-2rem))]',
          className,
        )}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  );
}
